import { useCallback, useEffect, useRef, useState } from "react";
import { ExternalLink, Loader2, Pause, Play, Plug, Send, ShieldCheck, Square, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { consumeTransfer, parseUsernames } from "../../lib/publisherTools";
import CaptchaSolver from "../../components/admin/CaptchaSolver";

const REC_STATUS = {
  pending: { label: "Bekliyor", cls: "text-[#64748B]" },
  sending: { label: "Gönderiliyor", cls: "text-cyan-300" },
  sent: { label: "Gönderildi", cls: "text-emerald-300" },
  failed: { label: "Başarısız", cls: "text-red-300" },
};

export default function BulkMessages() {
  const [accounts, setAccounts] = useState([]);
  const [accForm, setAccForm] = useState({ username: "", label: "", session: "" });
  const [reconnectId, setReconnectId] = useState(null);
  const [reconnectSession, setReconnectSession] = useState("");
  const [senderId, setSenderId] = useState("");
  const [message, setMessage] = useState("");
  const [recipientsText, setRecipientsText] = useState("");
  const [preview, setPreview] = useState(null);
  const [forceResend, setForceResend] = useState(false);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [checkingId, setCheckingId] = useState(null);
  const jobPollRef = useRef(null);

  const fetchAccounts = useCallback(() => {
    api.get("/admin/accounts").then((r) => {
      setAccounts(r.data);
      const active = r.data.find((a) => a.is_active);
      if (active) setSenderId((prev) => prev || active.id);
    }).catch(() => {});
  }, []);

  const fetchJob = useCallback(() => {
    api.get("/admin/messages/jobs/active").then((r) => setJob(r.data.job)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = consumeTransfer();
    if (t) { setRecipientsText((prev) => (prev ? prev + "\n" + t : t)); toast.success("Alıcılar aktarıldı"); }
    fetchAccounts(); fetchJob();
  }, [fetchAccounts, fetchJob]);

  useEffect(() => {
    if (job && ["running", "paused"].includes(job.status)) {
      jobPollRef.current = setInterval(fetchJob, 3000);
    }
    return () => clearInterval(jobPollRef.current);
  }, [job?.id, job?.status, fetchJob]);

  const addAccount = async () => {
    setVerifying(true);
    try {
      const r = await api.post("/admin/accounts", accForm);
      setAccForm({ username: "", label: "", session: "" });
      toast.success(r.data?.message || "TikTok hesabı kaydedildi");
      fetchAccounts();
    } catch (e) { toast.error(formatApiError(e)); }
    setVerifying(false);
  };

  const accountAction = async (id, action) => {
    try {
      if (action === "delete") {
        if (!window.confirm("Hesap silinsin mi?")) return;
        await api.delete(`/admin/accounts/${id}`);
      } else {
        await api.post(`/admin/accounts/${id}/use`);
      }
      toast.success("Güncellendi");
      fetchAccounts();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reconnect = async (id) => {
    if (!reconnectSession.trim()) { toast.error("Yeni oturum bilgisini girin"); return; }
    setVerifying(true);
    try {
      const r = await api.post(`/admin/accounts/${id}/session`, { session: reconnectSession.trim() });
      setReconnectId(null); setReconnectSession("");
      toast.success(r.data?.message || "Oturum yenilendi");
      fetchAccounts();
    } catch (e) { toast.error(formatApiError(e)); }
    setVerifying(false);
  };

  const testAccount = async (id) => {
    setCheckingId(id);
    try {
      const r = await api.post(`/admin/accounts/${id}/check`);
      toast.success(r.data?.message || "Oturum geçerli");
      fetchAccounts();
    } catch (e) { toast.error(formatApiError(e)); fetchAccounts(); }
    setCheckingId(null);
  };

  const recipientList = parseUsernames(recipientsText);

  const buildPreview = async () => {
    if (!senderId) { toast.error("Gönderen hesap seçin"); return; }
    if (!message.trim()) { toast.error("Mesaj yazın"); return; }
    if (!recipientList.length) { toast.error("En az bir alıcı girin"); return; }
    setBusy(true);
    try {
      const chk = await api.post("/admin/messages/check", { usernames: recipientList });
      setForceResend(false);
      setPreview({ already: chk.data.already_sent, total: recipientList.length });
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const startJob = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/messages/jobs", { account_id: senderId, message: message.trim(), recipients: recipientList, force_resend: forceResend });
      toast.success(`Gönderim başlatıldı: ${r.data.total} alıcı${r.data.skipped_already_sent.length ? ` · ${r.data.skipped_already_sent.length} tanesi daha önce gönderilmiş (atlandı)` : ""}`);
      setPreview(null);
      fetchJob();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const jobControl = async (action) => {
    if (!job) return;
    try {
      await api.post(`/admin/messages/jobs/${job.id}/control`, { action });
      fetchJob();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const inputCls = "w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-[#64748B] outline-none focus:border-cyan-400/60 transition-colors";
  const cardCls = "rounded-2xl border border-white/[0.07] bg-[#0A0E17] p-5";
  const btnCls = "inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-3.5 py-1.5 border transition-colors";

  return (
    <div data-testid="messages-page" className="space-y-6">
      <CaptchaSolver />
      <h1 className="font-display font-700 text-lg text-white flex items-center gap-2"><Send size={18} className="text-cyan-300" /> Toplu Mesaj</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className={cardCls}>
          <h4 className="text-[11px] font-bold text-cyan-300 mb-3 flex items-center gap-1.5"><Plug size={12} /> TİKTOK HESABI EKLE</h4>
          <div className="space-y-2.5">
            <input value={accForm.username} onChange={(e) => setAccForm({ ...accForm, username: e.target.value })} placeholder="Kullanıcı adı (@ olmadan)" data-testid="account-username-input" className={inputCls} />
            <input value={accForm.label} onChange={(e) => setAccForm({ ...accForm, label: e.target.value })} placeholder="Hesap adı / etiket (opsiyonel)" data-testid="account-label-input" className={inputCls} />
            <textarea value={accForm.session} onChange={(e) => setAccForm({ ...accForm, session: e.target.value })} rows={4} placeholder='Cookie-Editor ile dışa aktardığınız JSON dizisini buraya yapıştırın: [{"domain":".tiktok.com","name":"sessionid","value":"..."}, ...] veya sessionid=...; msToken=... formatı' data-testid="account-session-input" className={`${inputCls} resize-y font-mono text-[11px]`} />
            <button onClick={addAccount} disabled={verifying} data-testid="account-save-button" className="btn-glow inline-flex items-center gap-2 text-xs font-semibold text-white px-5 py-2.5 rounded-full disabled:opacity-50">{verifying && <Loader2 size={13} className="animate-spin" />}{verifying ? "TikTok'a giriş doğrulanıyor..." : "Hesabı Kaydet"}</button>
            <p className="text-[10px] text-[#64748B]">Tarayıcınızda TikTok'a giriş yapın → Cookie-Editor (veya benzeri) eklentiyle çerezleri <span className="text-cyan-300">JSON</span> olarak dışa aktarın → çıktının tamamını yukarıya yapıştırın. Kaydederken oturum TikTok'a gerçek giriş yapılarak doğrulanır (10-20 sn) ve şifreli saklanır.</p>
          </div>
        </div>

        <div className={cardCls}>
          <h4 className="text-[11px] font-bold text-cyan-300 mb-3">KAYITLI TİKTOK HESAPLARI</h4>
          {accounts.length === 0 ? (
            <p className="text-[11px] text-[#64748B]" data-testid="accounts-empty">Henüz hesap eklenmedi.</p>
          ) : (
            <div className="space-y-2" data-testid="accounts-list">
              {accounts.map((a) => (
                <div key={a.id} className="rounded-lg border border-white/[0.07] bg-black/40 p-2.5" data-testid={`account-row-${a.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-white mr-auto truncate">@{a.username}{a.label ? ` · ${a.label}` : ""}</span>
                    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold ${a.status === "active" ? "text-emerald-300" : "text-red-300"}`} data-testid={`account-status-${a.id}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${a.status === "active" ? "bg-emerald-400" : "bg-red-400"}`} />
                      {a.status === "active" ? "Bağlı" : "Oturum Geçersiz"}
                    </span>
                    {!a.is_active ? (
                      <button onClick={() => accountAction(a.id, "use")} data-testid={`account-use-${a.id}`} className="text-[10px] font-semibold text-cyan-300 hover:underline">Hesabı kullan</button>
                    ) : <span className="text-[10px] font-semibold text-cyan-300" data-testid={`account-inuse-${a.id}`}>Kullanımda</span>}
                    <button onClick={() => testAccount(a.id)} disabled={checkingId === a.id} data-testid={`account-test-${a.id}`} className="inline-flex items-center gap-1 text-[10px] font-semibold text-cyan-300 hover:underline disabled:opacity-50">
                      {checkingId === a.id ? <Loader2 size={11} className="animate-spin" /> : <ShieldCheck size={11} />} {checkingId === a.id ? "Test ediliyor..." : "Test Et"}
                    </button>
                    <button onClick={() => setReconnectId(reconnectId === a.id ? null : a.id)} data-testid={`account-reconnect-${a.id}`} className="text-[10px] font-semibold text-[#A0ABC0] hover:text-white">Yeniden bağla</button>
                    <button onClick={() => accountAction(a.id, "delete")} data-testid={`account-delete-${a.id}`} aria-label="Hesabı kaldır" className="text-[#64748B] hover:text-red-300 transition-colors"><Trash2 size={13} /></button>
                  </div>
                  {reconnectId === a.id && (
                    <div className="space-y-2 mt-2">
                      <textarea value={reconnectSession} onChange={(e) => setReconnectSession(e.target.value)} rows={3} placeholder='Yeni çerez JSON dizisi: [{"name":"sessionid","value":"..."}, ...]' data-testid="account-reconnect-input" className={`${inputCls} resize-y font-mono text-[11px]`} />
                      <button onClick={() => reconnect(a.id)} disabled={verifying} data-testid="account-reconnect-save" className="shrink-0 text-[11px] font-semibold text-cyan-300 border border-cyan-400/30 rounded-full px-3 py-1.5 hover:bg-cyan-400/10 disabled:opacity-50">{verifying ? "Doğrulanıyor..." : "Kaydet ve Doğrula"}</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={cardCls}>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] font-semibold text-[#A0ABC0] block mb-1.5">Gönderen Hesap</label>
            <select value={senderId} onChange={(e) => setSenderId(e.target.value)} data-testid="sender-select" className={inputCls}>
              <option value="">Hesap seçin...</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>@{a.username}{a.status !== "active" ? " (oturum geçersiz)" : ""}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-[#A0ABC0] block mb-1.5">Alıcı Kullanıcı Adları <span className="text-cyan-300">({recipientList.length})</span></label>
            <textarea value={recipientsText} onChange={(e) => setRecipientsText(e.target.value)} rows={5} placeholder={"kajapubg\noykkcm\ns2gsloxer"} data-testid="recipients-input" className={`${inputCls} resize-y font-mono text-xs`} />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-[#A0ABC0] block mb-1.5">Gönderilecek Mesaj</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="Merhaba, ajansımıza katılmak ister misin?" data-testid="message-input" className={`${inputCls} resize-none`} />
          </div>
          <button onClick={buildPreview} disabled={busy || (job && ["running", "paused"].includes(job.status))} data-testid="build-preview-button"
            className="btn-glow w-full inline-flex items-center justify-center gap-2 text-sm font-semibold text-white px-6 py-3.5 rounded-full disabled:opacity-50">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Gönderim Listesini Oluştur
          </button>
        </div>

        {preview && (
          <div className="mt-4 rounded-xl border border-cyan-400/25 bg-cyan-400/[0.05] p-4" data-testid="send-preview">
            <h4 className="text-xs font-bold text-white mb-2">Gönderim Önizleme</h4>
            <div className="text-[11px] text-[#A0ABC0] space-y-1">
              <p>Gönderen: <span className="text-white font-semibold" data-testid="preview-account">@{accounts.find((a) => a.id === senderId)?.username}</span></p>
              <p>Mesaj: <span className="text-white" data-testid="preview-message">{message.trim()}</span></p>
              <p>Alıcı sayısı: <span className="text-white font-semibold" data-testid="preview-total">{preview.total} kullanıcı</span></p>
              <p className="font-mono text-[10px] text-cyan-200/80 break-all" data-testid="preview-recipients">{recipientList.join(", ")}</p>
            </div>
            {preview.already.length > 0 && (
              <div className="mt-3 rounded-lg border border-amber-400/30 bg-amber-400/[0.06] p-3" data-testid="duplicate-warning">
                <p className="text-[11px] font-semibold text-amber-300">Bu kullanıcılara daha önce mesaj gönderilmiş ({preview.already.length}):</p>
                <p className="text-[10px] text-amber-200/70 mt-1 break-all">{preview.already.join(", ")}</p>
                <label className="flex items-center gap-2 mt-2 text-[11px] text-amber-200 cursor-pointer">
                  <input type="checkbox" checked={forceResend} onChange={(e) => setForceResend(e.target.checked)} data-testid="force-resend-checkbox" className="accent-amber-400 w-4 h-4" />
                  Yine de tekrar gönder
                </label>
              </div>
            )}
            <div className="flex gap-2 mt-3">
              <button onClick={startJob} disabled={busy} data-testid="send-confirm-button" className="btn-glow inline-flex items-center gap-2 text-xs font-semibold text-white px-5 py-2.5 rounded-full disabled:opacity-50">
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Gönderimi Başlat
              </button>
              <button onClick={() => setPreview(null)} data-testid="send-cancel-button" className="text-xs font-semibold text-[#A0ABC0] border border-white/10 px-5 py-2.5 rounded-full hover:text-white transition-colors">Vazgeç</button>
            </div>
          </div>
        )}

        {job && (
          <div className="mt-5 rounded-xl border border-white/[0.07] bg-black/30 p-4" data-testid="job-progress">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <h4 className="text-xs font-bold text-white mr-auto">Gönderim Durumu — @{job.account_username}</h4>
              <span className="text-[11px] text-cyan-300 font-semibold" data-testid="job-progress-text">{job.sent + job.failed} / {job.total} tamamlandı</span>
            </div>
            <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden mb-3" data-testid="job-progress-bar">
              <div className="h-full bg-gradient-to-r from-[#0047FF] to-[#00F0FF] transition-all" style={{ width: `${job.total ? ((job.sent + job.failed) / job.total) * 100 : 0}%` }} />
            </div>
            <div className="flex flex-wrap gap-4 text-[11px] font-semibold mb-4">
              <span className="text-emerald-300" data-testid="job-sent-count">Başarılı: {job.sent}</span>
              <span className="text-red-300" data-testid="job-failed-count">Başarısız: {job.failed}</span>
              <span className="text-[#A0ABC0]" data-testid="job-pending-count">Bekleyen: {job.pending}</span>
              <span className="text-[#64748B]" data-testid="job-status-label">Durum: {job.status === "running" ? "Çalışıyor" : job.status === "paused" ? "Duraklatıldı" : job.status === "done" ? "Tamamlandı" : "Durduruldu"}</span>
            </div>
            {["running", "paused"].includes(job.status) && (
              <div className="flex flex-wrap gap-2 mb-4">
                {job.status === "running" && <button onClick={() => jobControl("pause")} data-testid="job-pause-button" className={`${btnCls} text-amber-300 border-amber-400/30 hover:bg-amber-400/10`}><Pause size={12} /> Gönderimi Duraklat</button>}
                {job.status === "paused" && <button onClick={() => jobControl("resume")} data-testid="job-resume-button" className={`${btnCls} text-emerald-300 border-emerald-400/30 hover:bg-emerald-400/10`}><Play size={12} /> Devam Et</button>}
                <button onClick={() => jobControl("stop")} data-testid="job-stop-button" className={`${btnCls} text-red-300 border-red-400/30 hover:bg-red-400/10`}><Square size={12} /> Gönderimi Durdur</button>
              </div>
            )}
            <div className="overflow-x-auto" data-testid="job-recipients">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="text-[10px] text-[#64748B] border-b border-white/[0.07]">
                    <th className="py-2 pr-3">Kullanıcı</th><th className="py-2 pr-3">Durum</th><th className="py-2">Açıklama</th>
                  </tr>
                </thead>
                <tbody>
                  {job.recipients.map((r) => (
                    <tr key={r.username} className="border-b border-white/[0.04]" data-testid={`job-recipient-${r.username}`}>
                      <td className="py-2 pr-3 font-mono text-white">{r.username}</td>
                      <td className={`py-2 pr-3 font-semibold ${REC_STATUS[r.status]?.cls || ""}`}>{REC_STATUS[r.status]?.label || r.status}</td>
                      <td className="py-2 text-[#A0ABC0]">{r.status === "sent" ? "Başarılı" : r.error || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
